<template>
  <div class="card">
    <h2>Vetlles</h2>
    <button class="add-button" @click="goCreate">
      Nova Vetlla <font-awesome-icon icon="plus" />
    </button>
    <h4>En curs</h4>
    <ul class="vetllas">
      <li class="vetlla vetlla-headers">
        <span>Nom difunt</span>
        <span>Data Inici</span>
        <span>Data Fi</span>
        <span class="vetlla-cell--centrada">Sala</span>
        <span class="vetlla-cell--centrada">QR</span>
      </li>
      <li v-for="vetlla of vetllasCurso" class="vetlla vetlla--separator">
        <span>{{ vetlla.NombreDifunto }}</span>
        <span>{{ vetlla.FechaInicio }}</span>
        <span>{{ vetlla.FechaFin }}</span>
        <span class="vetlla-cell--centrada">{{
          salasIndexadasVetlla[vetlla.Id] ?? "-"
        }}</span>
        <span class="vetlla-cell--centrada">
          <span
            class="qr-badge"
            :class="vetlla.IsQrObert ? 'qr-badge--obert' : 'qr-badge--tancat'"
          >
            {{ vetlla.IsQrObert ? "Obert" : "Tancat" }}
          </span>
        </span>
        <div class="vetlla-buttons">
          <button
            class="vetlla-button"
            @click="goEditVetlla(vetlla.Id)"
            title="Editar vetlla"
          >
            <font-awesome-icon icon="pen" />
          </button>
          <!-- <button class="vetlla-button" @click="goDiapos(vetlla.Id)"> -->
          <!--   <font-awesome-icon icon="photo-film" /> -->
          <!-- </button> -->
          <button
            class="vetlla-button"
            @click="goLinks(vetlla.Id)"
            title="Veure enllaços de la vetlla"
          >
            <font-awesome-icon icon="link" />
          </button>
          <button
            class="vetlla-button vetlla-button--primary"
            @click="descarregaQrVetlla(vetlla)"
            title="Descarregar codi QR"
          >
            <font-awesome-icon icon="qrcode" />
          </button>
        </div>
      </li>
    </ul>
    <h4 class="expiradas-title">
      Expirades
      <font-awesome-icon
        class="vetllas-expand-icon"
        :class="{ 'vetllas-expand-icon--expanded': isExpanded }"
        icon="caret-up"
        @click="toggleVetllasExpand"
        title="Mostrar/ocultar vetlles expirades"
      />
    </h4>
    <div class="searchbar">
      <font-awesome-icon class="searchbar__icon" icon="magnifying-glass" />
      <input
        class="searchbar__input"
        type="text"
        placeholder="Nom difunt"
        v-model="searchExpirades"
      />
    </div>
    <ul class="vetllas">
      <li v-show="isExpanded" class="vetlla vetlla-headers">
        <span>Nom difunt</span>
        <span>Data Inici</span>
        <span>Data Fi</span>
      </li>
      <li
        v-for="vetlla of vetllasExpiradasExpansion"
        class="vetlla vetlla--separator"
      >
        <span>{{ vetlla.NombreDifunto }}</span>
        <span>{{ vetlla.FechaInicio }}</span>
        <span>{{ vetlla.FechaFin }}</span>
        <span></span>
        <div class="vetlla-buttons">
          <button
            class="vetlla-button"
            @click="goEditVetlla(vetlla.Id)"
            title="Editar vetlla"
          >
            <font-awesome-icon icon="pen" />
          </button>
          <button
            class="vetlla-button"
            @click="goPrint(vetlla.Id)"
            title="Descarregar llibre de condol"
          >
            <font-awesome-icon icon="print" />
          </button>
          <button
            class="vetlla-button"
            @click="downloadImages(vetlla.Id)"
            title="Descarregar imatges"
          >
            <font-awesome-icon icon="images" />
          </button>
        </div>
      </li>
    </ul>
    <QrDownloadModal
      :visible="showQrModal"
      :vetlla="vetllaSeleccionada"
      @close="showQrModal = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch, computed } from "vue";

import QrcodeVue from "qrcode.vue";
import QrDownloadModal from "../components/QrDownloadModal.vue";

import axios from "axios";

import { useRouter } from "vue-router";

import useErpApiClient from "../erp-api-client";
import { dateFromFechaString, getUploadPath } from "../helpers";
import { Vetlla, Sala } from "../types/vetlla.ts";
import CustomSwal from "../custom-swal.ts";
import { faFileZipper, faImages } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";

library.add(faFileZipper, faImages);

export default defineComponent({
  components: {
    QrcodeVue,
    QrDownloadModal,
  },
  setup() {
    const router = useRouter();
    const erpClient = useErpApiClient();

    const vetllas = ref<Vetlla[]>([]);
    const vetllasCurso = ref<Vetlla[]>([]);
    const vetllasExpiradas = ref<Vetlla[]>([]);
    const vetllaSeleccionada = ref<Vetlla | null>(null);
    const salas = ref<Sala[]>([]);
    const showQrModal = ref(false);

    const descarregaQrVetlla = (vetlla: Vetlla) => {
      vetllaSeleccionada.value = vetlla;
      showQrModal.value = true;
    };

    watch(vetllas, () => {
      const now = new Date().getTime();
      vetllasCurso.value = [];
      vetllasExpiradas.value = [];

      for (const vetlla of vetllas.value) {
        const fechaFin = vetlla.FechaFin;

        const dateFin = dateFromFechaString(fechaFin);
        if (dateFin.getTime() > now) {
          vetllasCurso.value.push(vetlla);
        } else {
          vetllasExpiradas.value.push(vetlla);
        }
      }
    });

    onMounted(() => {
      erpClient
        .request(() => axios.get("/api/vetlla/index"))
        .then((response) => {
          vetllas.value = response.data;
        });

      erpClient
        .request(() => axios.get("/api/sala/index"))
        .then((response) => {
          salas.value = response.data;
        });
    });

    const salasIndexadasVetlla = computed(() => {
      const salasIndx: {
        [key: number]: string;
      } = {};
      for (const sala of salas.value) {
        salasIndx[sala.IdVetlla] = sala.Nombre;
      }

      return salasIndx;
    });

    const goUpload = (idVetlla: Vetlla["Id"]) => {
      router.push(getUploadPath("vetlla", idVetlla));
    };

    const goEditVetlla = (idVetlla: Vetlla["Id"]) => {
      router.push({
        name: "vetllaEdit",
        params: {
          idVetlla: idVetlla,
        },
      });
    };

    const goDiapos = (idVetlla: Vetlla["Id"]) => {
      router.push({
        name: "diapositivas",
        params: {
          idVetlla: idVetlla,
        },
      });
    };

    const downloadImages = async (idVetlla: Vetlla["Id"]) => {
      try {
        const vetllaResponse = await axios.get(
          `/api/vetlla/read?id=${idVetlla}`,
        );
        const vetlla = vetllaResponse.data as Vetlla;

        const nomNet = vetlla.NombreDifunto.trim().replace(
          /[^a-zA-Z0-9]/g,
          "_",
        );
        const nomFitxer = `${nomNet}_imatges.zip`;

        const response = await axios.get(
          `/api/vetlla/download-images?id=${idVetlla}`,
          {
            responseType: "blob",
          },
        );

        const blob = new Blob([response.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", nomFitxer);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        CustomSwal.fire({
          icon: "error",
          title: "Error",
          text: "No s'han pogut descarregar les imatges.",
        });
        console.error(err);
      }
    };

    const goPrint = async (idVetlla: Vetlla["Id"]) => {
      try {
        const vetllaResponse = await axios.get(
          `/api/vetlla/read?id=${idVetlla}`,
        );
        const vetlla = vetllaResponse.data as Vetlla;

        const nomFitxer = `${vetlla.NombreDifunto.trim().replace(/\s+/g, "_")}_condol.pdf`;

        const response = await axios.get(
          `/api/condol/generate?id=${idVetlla}`,
          {
            responseType: "blob",
          },
        );

        const size = response.data.size;
        if (size === 0) {
          CustomSwal.fire({
            icon: "warning",
            title: "El llibre està buit",
            text: "No hi ha missatges visibles per descarregar.",
          });
        } else {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", nomFitxer);
          document.body.appendChild(link);
          link.click();
        }
      } catch (err) {
        CustomSwal.fire({
          icon: "error",
          title: "Error",
          text: "No s'ha pogut generar el llibre de condolències.",
        });
        console.error(err);
      }
    };

    const goCreate = () => {
      router.push({
        name: "vetllaCreate",
      });
    };

    const goLinks = (idVetlla: Vetlla["Id"]) => {
      router.push({
        name: "vetllaLinks",
        params: {
          idVetlla: idVetlla,
        },
      });
    };

    const isExpanded = ref(false);
    const toggleVetllasExpand = () => {
      isExpanded.value = !isExpanded.value;
    };

    const vetllasExpiradasExpansion = ref<Vetlla[]>([]);
    const searchExpirades = ref("");
    watch(searchExpirades, () => {
      if (searchExpirades.value) {
        isExpanded.value = true;
      } else {
        isExpanded.value = false;
      }

      const filtered = vetllasExpiradas.value.filter((v) => {
        const regex = new RegExp(`${searchExpirades.value}`, "g");
        if (v.NombreDifunto.match(regex)) {
          return true;
        } else {
          return false;
        }
      });

      vetllasExpiradasExpansion.value = filtered;
    });

    watch(isExpanded, () => {
      if (isExpanded.value) {
        vetllasExpiradasExpansion.value = vetllasExpiradas.value
          .slice(0, 1000)
          .reverse();
      } else {
        vetllasExpiradasExpansion.value = [];
      }
    });

    return {
      vetllasCurso,
      goUpload,
      goDiapos,
      goEditVetlla,
      goPrint,
      goCreate,
      goLinks,
      isExpanded,
      toggleVetllasExpand,
      vetllasExpiradasExpansion,
      searchExpirades,
      salasIndexadasVetlla,
      descarregaQrVetlla,
      showQrModal,
      vetllaSeleccionada,
      downloadImages,
    };
  },
});
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2em;

  padding: 1em;
  border-radius: $rounded-border;

  background-color: $bg-color;
}

.add-button {
  @include button($primary-color);
}

.vetllas {
  min-width: 48em;

  padding: 0;
  margin: 0;
}

.vetlla {
  width: 100%;

  display: grid;
  grid-template-columns: 3fr 2fr 2fr 1fr 1fr 2fr;

  background-color: $bg-color;

  list-style: none;

  padding: 1em;

  &--separator {
    border-top: $border-top-separator;
  }

  &:hover {
    background-color: color.adjust($bg-color, $lightness: -8%);
  }
}

.qr-badge {
  display: inline-block;
  padding: 0.2em 0.6em;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: bold;

  &--obert {
    background-color: #c6f6d5;
    color: #276749;
  }

  &--tancat {
    background-color: #fed7d7;
    color: #c53030;
  }
}

.vetlla-cell {
  &--centrada {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.vetlla-headers {
  list-style: none;
  font-weight: bold;
}

.vetlla > * {
  align-self: center;
}

.vetlla-buttons {
  justify-self: end;

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.6em;
}

.vetlla-button {
  @include icon-button($primary-bg-color);

  opacity: 0.85;
  transition: all 150ms ease-out;

  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }

  &--primary {
    @include icon-button($primary-color);
  }
}

.expiradas-title {
  display: flex;
  align-items: center;
  gap: 0.4em;
}

.vetllas-expand-icon {
  font-size: 1.4em;
  transform: rotate(0);
  transition: all 300ms ease-out;

  &--expanded {
    transform: rotate(180deg);
    transition: all 300ms ease-out;
  }

  &:hover {
    cursor: pointer;
  }
}

.searchbar {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.6em;

  &__icon {
    font-size: 1.4em;
    color: $text-color;
  }

  &__input {
    border: $input-border;
    border-radius: 256px;
    height: $input-height;
    padding: $input-padding;
    padding-left: 0.6em;

    &:focus {
      outline: none;
      border: 2px solid $primary-color;
    }
  }
}
</style>
